import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Loader2, Lock, Eye, EyeOff } from "lucide-react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useNavigate, useParams } from "@tanstack/react-router";
import { useDjRestAuthPasswordResetConfirmCreate } from "@/api/lctApIComponents";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";

export function ResetPasswordForm() {
  const { uid, token } = useParams({ from: "/password-reset/confirm/$uid/$token" });
  const navigate = useNavigate();
  const [passwordResetComfirmed, setPasswordResetComfirmed] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { toast } = useToast();
  const resetPasswordMutation = useDjRestAuthPasswordResetConfirmCreate({
    onSuccess: () => {
      setPasswordResetComfirmed(true);
    },
    onError(error: unknown) {
      // @ts-expect-error
      const errorMessages: Array<string> = Object.values(error?.stack) ?? [];
      const a = errorMessages.reduce((e: string | null, acc: string) => `${acc} ${e ?? ""}`, "") ?? null;

      toast({
        variant: "destructive",
        title: "Error!",
        description: `${a ? a : "Something went wrong, please try again"}`,
      });
    },
  });

  const formSchema = z
    .object({
      password1: z.string().min(8, "Password is not strong enough"),
      password2: z.string().min(8, "Password is not strong enough"),
    })
    .superRefine(({ password1, password2 }, ctx) => {
      if (password1 !== password2) {
        ctx.addIssue({ code: "custom", path: ["password2"], message: "Passwords do not match" });
      }
    });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      password1: "",
      password2: "",
    },
  });

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    resetPasswordMutation.mutate({
      body: { uid: uid, token: token, new_password1: values.password1, new_password2: values.password2 },
    });
  };

  if (passwordResetComfirmed) {
    return (
      <div className="flex w-full flex-col items-center justify-center gap-6">
        <div className="flex flex-col gap-2 text-center">
          <h1 className="font-heading text-[32px] font-semibold leading-[48px] text-black">New password saved!</h1>
          <p className="font-body text-[14px] font-normal leading-[24px] text-gray-800">You are now ready to log in</p>
        </div>
        <div className="flex h-[100px] w-[100px] items-center justify-center rounded-full bg-[#2d6532]">
          <Lock color="white" size={52} />
        </div>
        <Button
          variant="outline"
          onClick={() => navigate({ to: "/login", replace: true })}
          disabled={resetPasswordMutation.isPending}
          className="h-10 w-full rounded-[6px] border border-slate-200 bg-white px-4 py-2 font-body text-sm font-medium leading-6 text-gray-900 hover:bg-gray-50"
        >
          <ArrowLeft className="mr-2" size={16} />
          Back to Log In page
        </Button>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col gap-6">
      <div className="flex flex-col gap-2 text-center">
        <h1 className="font-heading text-[32px] font-semibold leading-[48px] text-black">Reset your password</h1>
        <p className="font-body text-[14px] font-medium leading-[21px] text-slate-700">
          Almost done. Enter your new password, and you&apos;re good to go
        </p>
      </div>

      <Form {...form}>
        <form id="reset-password" onSubmit={form.handleSubmit(onSubmit)} className="flex w-full flex-col gap-6">
          <FormField
            control={form.control}
            name="password1"
            render={({ field }) => (
              <FormItem className="flex flex-col gap-1.5">
                <FormLabel className="font-body text-base font-normal leading-7 text-gray-900">New password</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter new password"
                      {...field}
                      autoComplete="new-password"
                      spellCheck="false"
                      autoCapitalize="none"
                      className="h-12 w-full rounded-[6px] border border-slate-400 bg-white px-3 py-3 pr-10 font-body text-base font-normal leading-7 text-slate-900"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="password2"
            render={({ field }) => (
              <FormItem className="flex flex-col gap-1.5">
                <FormLabel className="font-body text-base font-normal leading-7 text-gray-900">Re-enter new password</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Input
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="Re-enter new password"
                      {...field}
                      autoComplete="new-password"
                      spellCheck="false"
                      autoCapitalize="none"
                      className="h-12 w-full rounded-[6px] border border-slate-400 bg-white px-3 py-3 pr-10 font-body text-base font-normal leading-7 text-slate-900"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex flex-col gap-6">
            <Button
              type="submit"
              disabled={resetPasswordMutation.isPending}
              className="h-10 w-full rounded-[6px] bg-[#2d6532] px-4 py-2 font-body text-sm font-semibold leading-6 text-white hover:bg-[#2d6532]/90"
            >
              {resetPasswordMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Reset password
            </Button>

            <Button
              variant="outline"
              onClick={() => navigate({ to: "/login", replace: true })}
              disabled={resetPasswordMutation.isPending}
              className="h-10 w-full rounded-[6px] border border-slate-200 bg-white px-4 py-2 font-body text-sm font-medium leading-6 text-gray-900 hover:bg-gray-50"
            >
              <ArrowLeft className="mr-2" size={16} />
              Back to Log In page
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
